import React, { useState, useEffect } from 'react';
import { collection, getDocs, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';

interface ExpenseReportsProps {
  domain: string;
}

interface Expense {
  id: string;
  category: string;
  description: string;
  amount: number;
  date: string;
  vendor?: string;
  paymentMode?: string;
  approvedBy?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt?: Timestamp;
}

interface CategorySummary {
  category: string;
  total: number;
  count: number;
  percentage: number;
}

const ExpenseReports: React.FC<ExpenseReportsProps> = ({ domain }) => {
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState<'week' | 'month' | 'quarter' | 'year'>('month');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'summary' | 'detailed'>('summary');

  const expenseCategories = [
    { id: 'salary', name: 'Salaries & Wages', icon: 'bi-people', color: '#5C2597' },
    { id: 'utilities', name: 'Utilities', icon: 'bi-lightning', color: '#FFC107' },
    { id: 'maintenance', name: 'Maintenance', icon: 'bi-tools', color: '#17A2B8' },
    { id: 'supplies', name: 'Office Supplies', icon: 'bi-box-seam', color: '#28A745' },
    { id: 'transport', name: 'Transport', icon: 'bi-bus-front', color: '#E83E8C' },
    { id: 'events', name: 'Events & Activities', icon: 'bi-calendar-event', color: '#6F42C1' },
    { id: 'infrastructure', name: 'Infrastructure', icon: 'bi-building', color: '#FD7E14' },
    { id: 'technology', name: 'Technology & IT', icon: 'bi-pc-display', color: '#2D7DD2' },
    { id: 'marketing', name: 'Marketing', icon: 'bi-megaphone', color: '#DC3545' },
    { id: 'other', name: 'Miscellaneous', icon: 'bi-three-dots', color: '#6C757D' },
  ];

  useEffect(() => {
    fetchExpenses();
  }, [domain, dateRange]);

  const getDateRangeStart = (): Date => {
    const now = new Date();
    switch (dateRange) {
      case 'week':
        return new Date(now.setDate(now.getDate() - 7));
      case 'month':
        return new Date(now.setMonth(now.getMonth() - 1));
      case 'quarter':
        return new Date(now.setMonth(now.getMonth() - 3));
      case 'year':
        return new Date(now.setFullYear(now.getFullYear() - 1));
      default:
        return new Date(now.setMonth(now.getMonth() - 1));
    }
  };

  const fetchExpenses = async () => {
    if (!domain) return;
    setLoading(true);

    try {
      const expensesRef = collection(db, `school-users/${domain}/expenses`);
      const snapshot = await getDocs(expensesRef);

      const expenseList: Expense[] = [];
      snapshot.forEach(doc => {
        expenseList.push({ id: doc.id, ...doc.data() } as Expense);
      });

      // If no data, generate sample data for demonstration
      if (expenseList.length === 0) {
        const sampleExpenses = generateSampleExpenses();
        setExpenses(sampleExpenses);
      } else {
        setExpenses(expenseList);
      }
    } catch (error) {
      console.error('Error fetching expenses:', error);
      // Use sample data on error
      setExpenses(generateSampleExpenses());
    } finally {
      setLoading(false);
    }
  };

  const generateSampleExpenses = (): Expense[] => {
    const samples: Expense[] = [];
    const now = new Date();

    expenseCategories.forEach((cat, idx) => {
      // Generate 2-5 expenses per category
      const count = Math.floor(Math.random() * 4) + 2;
      for (let i = 0; i < count; i++) {
        const date = new Date(now);
        date.setDate(date.getDate() - Math.floor(Math.random() * 30));

        samples.push({
          id: `sample_${cat.id}_${i}`,
          category: cat.id,
          description: `${cat.name} expense ${i + 1}`,
          amount: Math.floor(Math.random() * 50000) + 5000,
          date: date.toISOString().split('T')[0],
          vendor: `Vendor ${idx + 1}`,
          paymentMode: ['Cash', 'Bank Transfer', 'Cheque', 'UPI'][Math.floor(Math.random() * 4)],
          status: ['approved', 'pending', 'approved'][Math.floor(Math.random() * 3)] as 'approved' | 'pending',
        });
      }
    });

    return samples;
  };

  const filteredExpenses = expenses.filter(exp => {
    const expDate = new Date(exp.date);
    const rangeStart = getDateRangeStart();
    const matchesDate = expDate >= rangeStart;
    const matchesCategory = selectedCategory === 'all' || exp.category === selectedCategory;
    return matchesDate && matchesCategory;
  });

  const totalExpenses = filteredExpenses.reduce((sum, exp) => sum + exp.amount, 0);
  const approvedExpenses = filteredExpenses.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.amount, 0);
  const pendingExpenses = filteredExpenses.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.amount, 0);

  const categorySummary: CategorySummary[] = expenseCategories.map(cat => {
    const catExpenses = filteredExpenses.filter(e => e.category === cat.id);
    const total = catExpenses.reduce((sum, e) => sum + e.amount, 0);
    return {
      category: cat.name,
      total,
      count: catExpenses.length,
      percentage: totalExpenses > 0 ? Math.round((total / totalExpenses) * 100) : 0,
    };
  }).filter(c => c.total > 0).sort((a, b) => b.total - a.total);

  const formatCurrency = (amount: number): string => {
    if (amount >= 10000000) return `₹${(amount / 10000000).toFixed(2)} Cr`;
    if (amount >= 100000) return `₹${(amount / 100000).toFixed(2)} L`;
    if (amount >= 1000) return `₹${(amount / 1000).toFixed(1)} K`;
    return `₹${amount.toLocaleString('en-IN')}`;
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Summary Cards */}
      <div className="row g-3 mb-4">
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100 bg-primary text-white">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="small opacity-75">Total Expenses</div>
                  <div className="fs-3 fw-bold">{formatCurrency(totalExpenses)}</div>
                  <div className="small mt-2 opacity-75">
                    {filteredExpenses.length} transactions
                  </div>
                </div>
                <i className="bi bi-wallet2 fs-1 opacity-50"></i>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Approved</div>
                  <div className="fs-3 fw-bold text-success">{formatCurrency(approvedExpenses)}</div>
                  <div className="small text-muted mt-2">
                    {filteredExpenses.filter(e => e.status === 'approved').length} approved
                  </div>
                </div>
                <div className="rounded-circle bg-success bg-opacity-10 p-3">
                  <i className="bi bi-check-circle-fill text-success fs-4"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-4">
          <div className="card border-0 shadow-sm h-100">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small">Pending Approval</div>
                  <div className="fs-3 fw-bold text-warning">{formatCurrency(pendingExpenses)}</div>
                  <div className="small text-muted mt-2">
                    {filteredExpenses.filter(e => e.status === 'pending').length} pending
                  </div>
                </div>
                <div className="rounded-circle bg-warning bg-opacity-10 p-3">
                  <i className="bi bi-clock-fill text-warning fs-4"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-center">
            <div className="col-md-3">
              <label className="form-label small text-muted">Time Period</label>
              <select
                className="form-select"
                value={dateRange}
                onChange={(e) => setDateRange(e.target.value as 'week' | 'month' | 'quarter' | 'year')}
              >
                <option value="week">Last 7 Days</option>
                <option value="month">Last Month</option>
                <option value="quarter">Last Quarter</option>
                <option value="year">Last Year</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">Category</label>
              <select
                className="form-select"
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
              >
                <option value="all">All Categories</option>
                {expenseCategories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label small text-muted">View</label>
              <div className="btn-group w-100">
                <button
                  className={`btn ${viewMode === 'summary' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('summary')}
                >
                  Summary
                </button>
                <button
                  className={`btn ${viewMode === 'detailed' ? 'btn-primary' : 'btn-outline-primary'}`}
                  onClick={() => setViewMode('detailed')}
                >
                  Detailed
                </button>
              </div>
            </div>
            <div className="col-md-3 text-end">
              <label className="form-label small text-muted d-block">&nbsp;</label>
              <button className="btn btn-outline-success me-2">
                <i className="bi bi-download me-2"></i>Export
              </button>
              <button className="btn btn-outline-primary" onClick={fetchExpenses}>
                <i className="bi bi-arrow-clockwise"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {viewMode === 'summary' ? (
        /* Category-wise Summary */
        <div className="row g-4">
          <div className="col-lg-8">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-pie-chart me-2 text-primary"></i>
                  Category-wise Breakdown
                </h6>
              </div>
              <div className="card-body">
                {categorySummary.length === 0 ? (
                  <div className="text-center text-muted py-4">
                    <i className="bi bi-inbox fs-1 d-block mb-2"></i>
                    No expenses recorded for this period
                  </div>
                ) : (
                  <div className="row g-3">
                    {categorySummary.map((cat) => {
                      const catInfo = expenseCategories.find(c => c.name === cat.category);
                      return (
                        <div key={cat.category} className="col-md-6">
                          <div className="d-flex align-items-center p-3 bg-light rounded">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center me-3"
                              style={{
                                width: 48,
                                height: 48,
                                backgroundColor: `${catInfo?.color}20`,
                                color: catInfo?.color,
                              }}
                            >
                              <i className={`${catInfo?.icon} fs-5`}></i>
                            </div>
                            <div className="flex-grow-1">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-semibold">{cat.category}</span>
                                <span className="fw-bold">{formatCurrency(cat.total)}</span>
                              </div>
                              <div className="progress" style={{ height: 6 }}>
                                <div
                                  className="progress-bar"
                                  style={{
                                    width: `${cat.percentage}%`,
                                    backgroundColor: catInfo?.color,
                                  }}
                                ></div>
                              </div>
                              <div className="d-flex justify-content-between mt-1">
                                <small className="text-muted">{cat.count} transactions</small>
                                <small className="text-muted">{cat.percentage}%</small>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Top Expenses */}
          <div className="col-lg-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white py-3">
                <h6 className="mb-0 fw-bold">
                  <i className="bi bi-sort-down me-2 text-danger"></i>
                  Top Expenses
                </h6>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {filteredExpenses
                    .sort((a, b) => b.amount - a.amount)
                    .slice(0, 5)
                    .map((expense, idx) => {
                      const catInfo = expenseCategories.find(c => c.id === expense.category);
                      return (
                        <div key={expense.id} className="list-group-item">
                          <div className="d-flex align-items-center">
                            <div
                              className="rounded me-3 text-center"
                              style={{
                                width: 32,
                                height: 32,
                                lineHeight: '32px',
                                backgroundColor: `${catInfo?.color}20`,
                                color: catInfo?.color,
                              }}
                            >
                              {idx + 1}
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-semibold small">{expense.description}</div>
                              <small className="text-muted">{expense.date}</small>
                            </div>
                            <div className="text-end">
                              <div className="fw-bold">{formatCurrency(expense.amount)}</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Detailed Table View */
        <div className="card border-0 shadow-sm">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>Date</th>
                  <th>Category</th>
                  <th>Description</th>
                  <th>Vendor</th>
                  <th>Payment Mode</th>
                  <th className="text-end">Amount</th>
                  <th className="text-center">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredExpenses
                  .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
                  .map(expense => {
                    const catInfo = expenseCategories.find(c => c.id === expense.category);
                    return (
                      <tr key={expense.id}>
                        <td>{expense.date}</td>
                        <td>
                          <span className="d-flex align-items-center">
                            <i className={`${catInfo?.icon} me-2`} style={{ color: catInfo?.color }}></i>
                            {catInfo?.name || expense.category}
                          </span>
                        </td>
                        <td>{expense.description}</td>
                        <td>{expense.vendor || '-'}</td>
                        <td>{expense.paymentMode || '-'}</td>
                        <td className="text-end fw-semibold">{formatCurrency(expense.amount)}</td>
                        <td className="text-center">
                          <span className={`badge ${expense.status === 'approved' ? 'bg-success' : expense.status === 'pending' ? 'bg-warning' : 'bg-danger'}`}>
                            {expense.status}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
              <tfoot className="table-light">
                <tr>
                  <td colSpan={5} className="fw-bold">Total</td>
                  <td className="text-end fw-bold">{formatCurrency(totalExpenses)}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExpenseReports;
